import React, { useCallback, useMemo } from 'react';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { emptyString } from 'constants/emptyConst';
import { useFileValidationRules } from 'utils/hooks/useFileHandling';
import { BlockOutlined, CloudDoneOutlined, Publish } from '@mui/icons-material';
import { DropzoneAreaBaseWrapper, UploadedFileText } from './DocumentsStyles';
import { DropzoneAreaBase } from 'mui-file-dropzone/dist';

function UploadDocumentComponent(props) {
  const { attachmentId, onUpload, isUploaded, filesUploadInProgress, disabled, readOnly } = props;
  const { t } = useTranslation();
  const { maxSize } = useFileValidationRules();
  const specificAttachmentInProgress = useMemo(() => {
    return filesUploadInProgress.find(attachment => attachment.attachmentId === attachmentId && attachment.inProgress);
  }, [filesUploadInProgress, attachmentId]);

  const getUploadText = useCallback(() => {
    return (
      <>
        {specificAttachmentInProgress ? (
          <>
            {t('OfferEdit.Documents.Processing')}
            <br />
            <CircularProgress color='primary' size={22} />
          </>
        ) : (
          <>
            {isUploaded ? (
              <UploadedFileText disabledDropzone={disabled || readOnly}>
                <CloudDoneOutlined sx={theme => ({ fontSize: theme.spacing(3), mr: 0.75 })} />
                {t('OfferEdit.Documents.UploadedFile')}
              </UploadedFileText>
            ) : disabled ? (
              <>
                <BlockOutlined color='disabled' sx={theme => ({ fontSize: theme.spacing(3) })} />
                <br />
              </>
            ) : (
              <>
                <Publish sx={theme => ({ fontSize: theme.spacing(3) })} />
                <br />
              </>
            )}
            {disabled ? (
              <>{t('OfferEdit.Documents.NotUploadFile')}</>
            ) : (
              <>
                {t('OfferEdit.Documents.DragFile')}
                <br />
                <span style={{ color: '#0084FF', textDecoration: 'underline' }}>{t('OfferEdit.Documents.BrowseFile')}</span>
                <br />
                <span style={{ color: '#A3A3A3' }}>{t('OfferEdit.Documents.MaxFileSize', { maxSize })}</span>
              </>
            )}
          </>
        )}
      </>
    );
  }, [specificAttachmentInProgress, t, isUploaded, disabled, readOnly, maxSize]);

  return (
    <DropzoneAreaBaseWrapper disabledDropzone={disabled || readOnly} isUploaded={isUploaded}>
      <DropzoneAreaBase
        Icon={getUploadText}
        dropzoneText={emptyString}
        showAlerts={false}
        filesLimit={1}
        maxFileSize={Number.MAX_VALUE}
        onAdd={onUpload}
        onDrag={onUpload}
      />
    </DropzoneAreaBaseWrapper>
  );
}

UploadDocumentComponent.defaultProps = {
  disabled: false,
  isUploaded: false,
  filesUploadInProgress: []
};

UploadDocumentComponent.propTypes = {
  attachmentId: PropTypes.number.isRequired,
  onUpload: PropTypes.func.isRequired,
  isUploaded: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  filesUploadInProgress: PropTypes.array.isRequired
};

export default UploadDocumentComponent;
